import type { UserStatsEventModelDTO } from '@capture/client-api/src/orval'
import type {
    AccountInfoResponse,
    ClientStats,
    UpsertUserAccountAttribute,
} from '~/@types/backend-types'
import type { Dispatch } from '~/state/common/actions'
import { BulkOfActions } from '~/state/common/actions'
import {
    AccountAttributesStatus,
    setAccountAttribute,
    setAccountAttributes,
    setAccountAttributesError,
    setAccountAttributesStatus,
} from '~/state/currentUser/accountAttributesSlice'
import {
    FetchedAccountInfo,
    FetchedFileCount,
    StartFetchingAccountInfo,
    UnableToFetchAccountInfo,
} from '~/state/currentUser/actions'
import {
    ProfileNameChangeFailed,
    ProfileNameChanged,
    ProfilePictureChangeFailed,
    ProfilePictureChangeStarted,
} from '~/state/users/actions'
import { handleUnknownError } from '~/utilities/errorUtils'
import { getServiceProvider } from './HostProvider'
import { clearLoginInfo } from './externals'

export const fetchAccountInfo = async (
    dispatch: Dispatch,
): Promise<AccountInfoResponse> => {
    try {
        dispatch(StartFetchingAccountInfo())
        const service =
            getServiceProvider().getAppServiceForLoggedInUserDefaults()
        const accountInfo = await service.getAccountInfo()
        dispatch(
            FetchedAccountInfo({
                allowAnalytics:
                    accountInfo.permitted_services?.analytics ?? false,
                auth_token: service.authToken,
                file_type_counters: accountInfo.file_type_counters,
                isBeingShutDown: Boolean(accountInfo.shutdown_info),
                is_read_only_user: accountInfo.is_read_only_user ?? false,
                logged_in_as: accountInfo.logged_in_as,
                max_space: parseInt(accountInfo.max_space, 10),
                name: accountInfo.name,
                number_of_subscribed_albums:
                    accountInfo.number_of_subscribed_albums ?? 0,
                profile_picture: accountInfo.profile_picture_url ?? undefined,
                sunset_delete_date: accountInfo.sunset_delete_date,
                tos_to_approve: accountInfo.tos_to_approve ?? null,
                used_space: parseInt(accountInfo.used_space, 10),
                uuid: accountInfo.uuid,
            }),
        )
        return accountInfo
    } catch (e) {
        clearLoginInfo()
        dispatch(UnableToFetchAccountInfo())
        throw e
    }
}

export const setProfileUserName = async (dispatch: Dispatch, name: string) => {
    try {
        const service =
            getServiceProvider().getAppServiceForLoggedInUserDefaults()
        await service.setProfileName(name)
        dispatch(ProfileNameChanged({ name }))
    } catch (_e) {
        dispatch(ProfileNameChangeFailed())
    }
}

export const setProfilePicture = async (dispatch: Dispatch, file: Blob) => {
    try {
        const service =
            getServiceProvider().getAppServiceForLoggedInUserDefaults()
        dispatch(ProfilePictureChangeStarted())
        await service.setProfilePictureFromBlob(file)
        await fetchAccountInfo(dispatch)
    } catch (_e) {
        dispatch(ProfilePictureChangeFailed())
    }
}

export const refreshFileCount = async (
    dispatch: Dispatch,
    timelineId?: JobID,
) => {
    try {
        const service =
            getServiceProvider().getAppServiceForLoggedInUserDefaults()
        const { file_type_counters } = await fetchAccountInfo(dispatch)

        // CAPWEB-3121: remove getTimelineMonths call when getAccountInfo response includes `document_count`
        const { months } = await service.getTimelineMonths(
            timelineId ?? (await service.getDefaultJob()).id,
        )

        const documentCount = months.reduce(
            (count, month) => count + month.document_count,
            0,
        )
        const countersWithDocs = {
            ...file_type_counters,
            document_count: documentCount,
        }
        dispatch(
            FetchedFileCount({
                file_type_counters: countersWithDocs,
            }),
        )
    } catch (_error) {
        /* empty */
    }
}

export const sendUserStatistics = async (events: UserStatsEventModelDTO[]) => {
    try {
        const service =
            getServiceProvider().getAppServiceForLoggedInUserDefaults()
        await service.postUserStatistics(events)
    } catch (_e) {
        /* empty */
    }
}

export const sendClientStats = async (data: ClientStats) => {
    try {
        const service =
            getServiceProvider().getAppServiceForLoggedInUserDefaults()
        await service.postClientStats(data)
    } catch (_e) {
        /* empty */
    }
}

export const fetchAccountAttributes = async (
    dispatch: Dispatch,
): Promise<void> => {
    try {
        dispatch(setAccountAttributesStatus(AccountAttributesStatus.FETCHING))
        const service =
            getServiceProvider().getAppServiceForLoggedInUserDefaults()
        const result = await service.getAccountAttributes()
        dispatch(
            BulkOfActions([
                setAccountAttributes(result),
                setAccountAttributesStatus(AccountAttributesStatus.SUCCEEDED),
            ]),
        )
    } catch (error) {
        let errorMessage = 'unknown Error'
        if (typeof error === 'string') {
            errorMessage = error
        } else if (error instanceof Error) {
            errorMessage = error.message
        }
        dispatch(
            BulkOfActions([
                setAccountAttributesStatus(AccountAttributesStatus.FAILED),
                setAccountAttributesError(errorMessage),
            ]),
        )
    }
}

export const upsertAccountAttributes = async (
    options: UpsertUserAccountAttribute,
    dispatch: Dispatch,
) => {
    try {
        dispatch(setAccountAttributesStatus(AccountAttributesStatus.FETCHING))
        const service =
            getServiceProvider().getAppServiceForLoggedInUserDefaults()
        const result = await service.upsertAccountAttributes(options)
        dispatch(
            BulkOfActions([
                setAccountAttribute(result[0]),
                setAccountAttributesStatus(AccountAttributesStatus.SUCCEEDED),
            ]),
        )
    } catch (error) {
        const errorMessage = handleUnknownError(error)
        dispatch(
            BulkOfActions([
                setAccountAttributesStatus(AccountAttributesStatus.FAILED),
                setAccountAttributesError(errorMessage),
            ]),
        )
    }
}
